import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { formatApiError } from "@/lib/api";
import { Loader2 } from "lucide-react";
import haryanaLogo from "@/assets/haryana-logo.png";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [identifier, setIdentifier] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(identifier, password);
      navigate("/");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen bg-slate-50">
      <div className="flex-1 flex items-center justify-center p-6">
        <div className="w-full max-w-md">
          <div className="flex items-center gap-3 mb-8">
            <img src={haryanaLogo} alt="Government of Haryana" className="h-14 w-auto" />
            <div>
              <h1 className="font-heading font-bold text-lg text-slate-900 leading-tight">Swachhatam League Haryana</h1>
              <p className="text-xs text-slate-500 tracking-wide">Sanitation & Cleanliness Ranking Portal</p>
            </div>
          </div>

          <h2 className="font-heading text-2xl font-bold text-slate-900 mb-1">Sign in</h2>
          <p className="text-sm text-slate-500 mb-6">Access the assessment dashboard</p>

          <form onSubmit={submit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="identifier">Email or Username</Label>
              <Input id="identifier" type="text" value={identifier} onChange={(e) => setIdentifier(e.target.value)} className="h-12" placeholder="you@example.com or username" data-testid="login-email" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <Input id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} className="h-12" placeholder="••••••••" data-testid="login-password" />
            </div>
            {error && <p className="text-sm text-red-500" data-testid="login-error">{error}</p>}
            <Button type="submit" disabled={loading} className="w-full h-12 bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="login-submit">
              {loading && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
              Sign In
            </Button>
          </form>
        </div>
      </div>
      <div className="hidden lg:flex flex-1 relative bg-slate-900">
        <img
          src="https://images.unsplash.com/photo-1635176061729-35ca541a03a1?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2ODl8MHwxfHNlYXJjaHwxfHxjbGVhbiUyMGdyZWVuJTIwY2l0eSUyMHN0cmVldCUyMHRyZWV8ZW58MHx8fHwxNzg3NjUwOTQzfDA&ixlib=rb-4.1.0&q=85"
          alt="Clean green city"
          className="absolute inset-0 h-full w-full object-cover opacity-70"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-slate-900/90 to-slate-900/30" />
        <div className="relative z-10 flex flex-col justify-end p-12 text-white">
          <h2 className="font-heading text-3xl font-bold mb-3">Ranking & Recognition of Municipalities</h2>
          <p className="text-slate-200 max-w-md">Assess sanitation and cleanliness across Haryana municipalities with structured, field-ready assessment forms.</p>
        </div>
      </div>
    </div>
  );
}
